import type { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import type { ScenarioData } from "@Shared/types/ScenarioTypes";
import appStorage from "@Storage";

export type ScenarioRecoveryData = {
  data: ScenarioData;
  savedAt: number;
  scenarioId: string;
};

const RECOVERY_STORAGE_KEY_PREFIX = "builder_scenario_recovery_draft";

const warnStorageFailure = (operation: string, error: unknown) => {
  console.warn(`[builder-recovery] Storage ${operation} failed`, error);
};

export const buildScenarioRecoveryId = (metadata?: ScenarioResponse | null): string | null => {
  if (metadata?.doc_id) return metadata.doc_id;
  if (metadata?.id) return metadata.id.toString();

  return null;
};

export const buildScenarioRecoveryKey = (scenarioId?: string | null): string | null => {
  const normalizedScenarioId = scenarioId?.trim();

  if (!normalizedScenarioId) return null;

  return `${RECOVERY_STORAGE_KEY_PREFIX}:${normalizedScenarioId}`;
};

const isScenarioData = (data: unknown): data is ScenarioData => {
  if (!data) return false;

  const scenarioData = data as Partial<ScenarioData>;
  return (
    !!scenarioData.InitialBallPos &&
    Array.isArray(scenarioData.Players) &&
    Array.isArray(scenarioData.BallFrames) &&
    Array.isArray(scenarioData.DebugBallFrames)
  );
};

const isRecoveryDraft = (draft: unknown): draft is ScenarioRecoveryData => {
  if (!draft) return false;

  const recoveryDraft = draft as Partial<ScenarioRecoveryData>;
  return isScenarioData(recoveryDraft.data);
};

export const saveScenarioRecoveryDraft = (scenarioId: string | null | undefined, data: ScenarioData): boolean => {
  const key = buildScenarioRecoveryKey(scenarioId);
  const normalizedScenarioId = scenarioId?.trim();
  if (!key || !normalizedScenarioId || !isScenarioData(data)) return false;

  try {
    appStorage.set({
      key,
      data: {
        data,
        scenarioId: normalizedScenarioId,
        savedAt: Date.now(),
      } satisfies ScenarioRecoveryData,
    });
    return true;
  } catch (error) {
    warnStorageFailure("save", error);
    return false;
  }
};

export const loadScenarioRecoveryDraft = (scenarioId: string | null | undefined): ScenarioRecoveryData | null => {
  const key = buildScenarioRecoveryKey(scenarioId);
  if (!key) return null;

  try {
    const draft = appStorage.get({ key });
    if (!isRecoveryDraft(draft)) {
      return null;
    }

    return draft;
  } catch (error) {
    warnStorageFailure("load", error);
    return null;
  }
};

export const clearScenarioRecoveryDraft = (scenarioId: string | null | undefined): void => {
  const key = buildScenarioRecoveryKey(scenarioId);
  if (!key) return;

  try {
    appStorage.remove({ key });
  } catch (error) {
    warnStorageFailure("clear", error);
  }
};
